import React, { useState, useEffect } from 'react';
import { Store, StoreOpeningTime } from 'shared/types/cart';
import Modal from 'components/commercetools-ui/atoms/modal';
import { useFormat } from 'helpers/hooks/useFormat';
import { useCart } from 'frontastic';
import GoogleMapComponent from './component/google-map';
import StoreList from './component/store-list';
import initialStores from './data';
import { Address } from '../steps/sections/addresses/types';

const CollectInStore: React.FC<{
  isOpen: boolean;
  setIsOpen: (arg: boolean) => void;
  setSameShippingAddress: (arg: boolean) => void;
  setShippingAddress: (arg: Address) => void;
  active: number;
}> = ({ isOpen = false, setIsOpen, setSameShippingAddress, setShippingAddress, active }) => {
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [stores, setStores] = useState<Store[]>(initialStores);
  const { getStores } = useCart();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { formatMessage } = useFormat({ name: 'checkout' });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [storeOpeningTime, setStoreOpeningTime] = useState<StoreOpeningTime | null>(null);
  const { getStoreOpeningTime } = useCart();

  useEffect(() => {
    setStoreOpeningTime(null);
    const fetchStoreOpeningTime = async () => {
      try {
        const storeOpeningTime = await (getStoreOpeningTime?.(selectedStore?.googlePlaceID ?? '') ??
          Promise.resolve([]));
        setStoreOpeningTime(storeOpeningTime);
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      }
    };

    if (selectedStore && selectedStore?.googlePlaceID) {
      fetchStoreOpeningTime();
    }
  }, [selectedStore, getStoreOpeningTime]);

  useEffect(() => {
    if (active == 1) {
      setIsOpen(true);
      setModalIsOpen(true);
    }
  }, []);

  useEffect(() => {
    const fetchStoresAndRelatedData = async () => {
      try {
        if (!getStores) return;
        const storeData = await getStores();
        if (!storeData.length) {
          setStores(initialStores);
          return;
        }
        setStores(storeData);
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      }
    };
    fetchStoresAndRelatedData();
  }, [getStores]);

  useEffect(() => {
    setModalIsOpen(isOpen);
  }, [isOpen]);

  const handleMarkerClick = (store: Store) => {
    setSelectedStore(store);
  };

  const closeModal = () => {
    setIsOpen(false);
    setModalIsOpen(false);
    setSelectedStore(null);
    setSearchKeyword('');
  };

  const handleStoreAddress = () => {
    const storeData = {
      addressId: selectedStore?.ID || '',
      firstName: '',
      lastName: '',
      email: selectedStore?.email || '',
      phone: selectedStore?.phone || '',
      line1: selectedStore?.address1 || '',
      line2: selectedStore?.address2 || '',
      postalCode: selectedStore?.postalCode || '',
      country: selectedStore?.countryCode || '',
      city: selectedStore?.city || '',
    } as Address;
    setShippingAddress({ ...storeData });
    setSameShippingAddress(false);
    closeModal();
  };

  return (
    <Modal
      shouldCloseOnOverlayClick={true}
      isOpen={modalIsOpen}
      style={{ content: { background: 'white', maxWidth: '960px' } }}
      contentLabel={formatMessage({ id: 'select.your.local.store', defaultMessage: 'Select your local store' })}
      onRequestClose={closeModal}
      className="modal-mobile-style my-2 box-border w-[96%] overflow-y-scroll md:h-[655px] md:w-auto md:overflow-hidden"
    >
      <>
        <div
          className="group absolute right-18 top-10 z-10 w-auto cursor-pointer rounded bg-white px-10 py-2 md:top-10"
          onClick={closeModal}
        >
          <span className="text-sm font-light group-hover:font-normal">Close</span>{' '}
          <span className="text-sm text-ag_color-red group-hover:font-normal">x</span>
        </div>
        <div className="flex h-[90vh] flex-col gap-18 p-18 md:h-auto md:flex-row md:overflow-hidden md:p-30">
          <div className="h-[45vh] w-full overflow-scroll md:h-auto md:w-3/10 md:overflow-hidden">
            <StoreList
              setSelectedStore={setSelectedStore}
              setSearchKeyword={setSearchKeyword}
              selectedStore={selectedStore}
              storeOpeningTime={storeOpeningTime}
              handleStoreAddress={handleStoreAddress}
            />
          </div>
          <div className="mt-8 w-full md:w-7/10 md:p-0">
            {stores && (
              <GoogleMapComponent
                stores={stores}
                selectedStore={selectedStore}
                setSelectedStore={setSelectedStore}
                handleMarkerClick={handleMarkerClick}
                postcode={searchKeyword}
                setSearchKeyword={setSearchKeyword}
                storeOpeningTime={storeOpeningTime}
                setStoreOpeningTime={setStoreOpeningTime}
                handleStoreAddress={handleStoreAddress}
              />
            )}
          </div>
        </div>
      </>
    </Modal>
  );
};

export default CollectInStore;
